import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';

import useScanCounters from '../../../hooks/useScanCounters';
import { usePermissions } from '../../../context/PermissionContext';
import { STAFF_ROLES } from '../../../constants/staff';
import { counterBadge, typeBadgeClass } from '../../../utils/scanCounters';
import ScanCounterPrintCard from './ScanCounterPrintCard';
import ShowToPatientOverlay from './ShowToPatientOverlay';
import { SCAN_SHARE_COPY } from '../../../constants/abdmCopy';

const SHOWN = 6;
const btnO = 'px-2.5 py-1 rounded-md border border-teal-600 dark:border-teal-500 bg-white dark:bg-slate-800 text-teal-800 dark:text-teal-200 text-xs font-semibold hover:bg-teal-50 dark:hover:bg-slate-700';

/**
 * Scan & Share tab body (R145/R148): the org's counter QR cards, nothing else. Counters come from
 * Settings; without HIP ID + counters staff see a set-up message (admins also get the link when the
 * host page passes onSetUpCounters - this component has no router dependency).
 */
export default function ScanShareCounterCards({ onSetUpCounters }) {
  const { role } = usePermissions();
  const isAdmin = String(role || '').toUpperCase() === STAFF_ROLES.ADMIN;
  const { counters, loaded, failed, retry } = useScanCounters(true);
  const [printing, setPrinting] = useState(null);
  const [showing, setShowing] = useState(null);
  const [q, setQ] = useState('');
  const [expanded, setExpanded] = useState(false);

  if (failed) {
    return (
      <div className="rounded-xl border border-amber-500 bg-amber-50 dark:bg-amber-900/40 text-amber-900 dark:text-amber-100 p-3 text-sm" data-testid="ssc-cards-error">
        Could not load the Scan &amp; Share counters.{' '}
        <button type="button" onClick={retry} className="underline font-semibold">Try again</button>
      </div>
    );
  }
  if (!loaded) return <p className="text-sm text-slate-600 dark:text-slate-300" data-testid="ssc-cards-loading">Loading counters…</p>;

  const ready = counters.filter((c) => c.qrText);
  if (ready.length === 0) {
    return (
      <div className="rounded-xl border-2 border-dashed border-slate-400 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 p-6 text-center text-slate-700 dark:text-slate-200" data-testid="ssc-cards-empty">
        <b>Set up your Scan &amp; Share counters in Settings</b>
        {isAdmin && onSetUpCounters && (
          <div className="mt-3">
            <button type="button" data-testid="ssc-cards-settings-link" onClick={onSetUpCounters} className="underline font-semibold text-teal-800 dark:text-teal-300">
              {SCAN_SHARE_COPY.settingsLink}
            </button>
          </div>
        )}
      </div>
    );
  }

  const needle = q.trim().toLowerCase();
  const matches = needle
    ? ready.filter((c) => c.counterId.toLowerCase().includes(needle) || c.name.toLowerCase().includes(needle))
    : ready;
  const visibleCards = expanded || needle ? matches : matches.slice(0, SHOWN);
  const hidden = matches.length - visibleCards.length;

  return (
    <div data-testid="ssc-cards">
      {ready.length > SHOWN && (
        <input
          aria-label="Search counters"
          placeholder="Search counters"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="ssc-cards-search"
          className="mb-3 px-2.5 py-2 w-56 rounded-lg border border-slate-400 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-sm"
        />
      )}
      <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}>
        {visibleCards.map((c) => (
          <div key={c.counterId} data-testid="ssc-card" className="rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 p-3 text-center">
            <div className="inline-block bg-white p-1 border border-slate-300 rounded leading-[0]">
              <QRCodeSVG value={c.qrText} size={140} level="M" />
            </div>
            <div className="mt-1.5 font-bold text-slate-900 dark:text-slate-50">{c.name}</div>
            <span className={`inline-block px-2 py-0.5 rounded-full border text-[11px] font-semibold ${typeBadgeClass(c.type)}`}>
              {counterBadge(c.counterId, c.type)}
            </span>
            <div className="mt-2 flex justify-center gap-2">
              <button type="button" data-testid="ssc-card-show" onClick={() => setShowing(c)} className="px-2.5 py-1 rounded-md bg-teal-600 text-white text-xs font-semibold">Show to patient</button>
              <button type="button" data-testid="ssc-card-print" onClick={() => setPrinting(c)} className={btnO}>Print</button>
            </div>
          </div>
        ))}
      </div>
      {hidden > 0 && (
        <div className="mt-3 text-center">
          <button type="button" data-testid="ssc-cards-more" onClick={() => setExpanded(true)} className={btnO}>+{hidden} more counter{hidden === 1 ? '' : 's'}</button>
        </div>
      )}
      {needle && matches.length === 0 && <p className="text-sm text-slate-600 dark:text-slate-300" data-testid="ssc-cards-nomatch">No counters match your search.</p>}
      <p className="mt-2 text-xs text-slate-600 dark:text-slate-300">
        Patients scan the QR at their counter with any ABHA app. Their profile then shows in the waiting list above.
      </p>
      {printing && <ScanCounterPrintCard counter={printing} onClose={() => setPrinting(null)} />}
      {showing && <ShowToPatientOverlay counter={showing} onClose={() => setShowing(null)} />}
    </div>
  );
}
